import type { Case } from "../api.js";

/**
 * WHICH PIECE OF LAND THIS REQUEST IS ABOUT.
 *
 * A case identified itself by nothing but a UUID. A buyer with two plots under
 * offer saw two identical cards and had to remember which UUID they submitted
 * when — with real consequences, since sending a deed reference into the wrong
 * request is a mistake nothing on screen would catch.
 *
 * The attorney's version of the same blindness was worse: the person told "the
 * attorney does the search at the land registry" could not see the district,
 * village or survey plan the client had typed, on the screen where they hold
 * the case.
 *
 * "THE LAND YOU DESCRIBED", not "the land". Every field here was typed by the
 * client from their own papers; none of it has been checked against a registry,
 * and checking it is the entire job the case exists to do. A heading that
 * simply asserted the parcel would present unverified client input as an
 * established fact, which is the one thing this product must never do.
 *
 * Renders nothing at all when the server sent no parcel — a caller not scoped
 * to it, or a request where the client has not yet said where the land is. An
 * empty panel headed "the land you described" under a case with nothing in it
 * would be worse than its absence.
 */
/**
 * The single most identifying line available, or null if the client gave
 * nothing usable. Exported so the professional's table cell and the panel below
 * cannot drift into describing the same parcel two different ways.
 *
 * Most specific first: a lot on a survey plan pins a parcel far more tightly
 * than a district does, and the first line is what a person scans.
 */
export function parcelHeadline(parcel: NonNullable<Case["parcel"]>): string | null {
  const lot = parcel.surveyPlanNo
    ? parcel.lotNo
      ? `Lot ${parcel.lotNo} on plan ${parcel.surveyPlanNo}`
      : `Survey plan ${parcel.surveyPlanNo}`
    : null;
  const place = [parcel.village, parcel.district].filter(Boolean).join(", ") || null;
  return lot ?? place ?? parcel.address ?? null;
}

export function ParcelIdentity({ caseRow, viewer }: { caseRow: Case; viewer: "client" | "professional" }) {
  const parcel = caseRow.parcel;
  if (!parcel) return null;

  const lot = parcel.surveyPlanNo
    ? parcel.lotNo
      ? `Lot ${parcel.lotNo} on plan ${parcel.surveyPlanNo}`
      : `Survey plan ${parcel.surveyPlanNo}`
    : null;
  const place = [parcel.village, parcel.district].filter(Boolean).join(", ") || null;
  const headline = parcelHeadline(parcel);

  // Nothing usable was given. Say so rather than rendering an empty heading.
  if (!headline) return null;

  const rest = [lot ? place : null, parcel.address].filter(Boolean);

  return (
    <div className="parcel-identity">
      <p className="eyebrow">
        {viewer === "client" ? "The land you described" : "The land as the client described it"}
      </p>
      <p className="parcel-headline">{headline}</p>
      {rest.length > 0 ? <p className="muted">{rest.join(" · ")}</p> : null}
      {viewer === "professional" ? (
        <p className="muted">
          <small>
            Client-supplied and unverified — establishing what the registry actually holds is
            the search itself.
          </small>
        </p>
      ) : null}
    </div>
  );
}
